package dev.lingvo.site.i18n

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import dev.lingvo.shared.textDirection
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import org.w3c.xhr.XMLHttpRequest

/**
 * The product's own strings, so Lingvo can be translated with Lingvo.
 *
 * **The English text stays at the call site.** Every lookup is `t("key", "English")`, not
 * `t("key")`, and that is the whole design rather than a convenience:
 *
 *  - A missing key, a failed catalogue fetch, or a locale nobody has translated yet renders English
 *    rather than a blank or a raw key. There is no state of this app in which a button has no label.
 *  - The source text a translator sees is the text a reader sees, and the two cannot drift, because
 *    the catalogue is generated *from* these fallbacks rather than maintained beside them.
 *  - A reviewer reading the call site still reads the sentence. Replacing copy with opaque keys is
 *    what makes a codebase's own UI unreadable to the people writing it.
 *
 * The cost is duplication between the call sites and `en.json`, which is why `en.json` is generated
 * by `scripts/extract-strings.mjs` and never hand-edited — the generator is what keeps them equal.
 *
 * Keys are dot-namespaced by surface and then by role: `pricing.hero.title`, `app.rail.settings`,
 * `editor.qa.empty`. They are stable identifiers, not sentences: renaming a key orphans its
 * translations, so a key outlives any wording change to the string it names.
 */
object I18n {

    /**
     * BCP-47 tag of the catalogue in use. `en` means "the fallbacks at the call sites".
     *
     * **Resolved once at boot and never observed.** It was Compose state, so that a switch would
     * recompose every string in place. That cost more than it bought: writing it after composition
     * had started re-entered Silk's initialiser and threw "duplicate keyframe name: lingvo-enter",
     * which left the app stuck in its loading shell — reproduced with the network removed entirely,
     * so it was the state write and not the fetch. A UI-language switch is a rare, deliberate act
     * and a page reload is an honest way to serve it; see [switchTo].
     */
    var locale: String = DEFAULT_LOCALE
        private set

    /**
     * Plain map, not a Compose state map, and that is deliberate.
     *
     * Nothing needs to observe the catalogues themselves: a catalogue is installed once, before the
     * locale that uses it is selected, and [locale] is the single observable that drives every
     * string on screen to recompose. Making this observable as well put a second write into the
     * same frame and re-entered Silk's initialiser, which throws
     * "User is registering duplicate keyframe name: lingvo-enter" and left the app stuck in its
     * loading shell — English worked only because English never registers anything.
     */
    private val catalogues = mutableMapOf<String, Map<String, String>>()

    /**
     * Installs a catalogue. Called once per locale, from generated code or a fetched JSON payload.
     *
     * Replaces rather than merges: a re-registration is a newer copy of the same catalogue, and
     * merging would let a key deleted upstream survive locally forever.
     */
    fun register(locale: String, entries: Map<String, String>) {
        catalogues[locale] = entries
    }

    /** Every locale with a catalogue installed, English included. */
    fun available(): Set<String> = catalogues.keys + DEFAULT_LOCALE

    /**
     * Switches locale and remembers the choice.
     *
     * Unknown or unregistered tags fall back to English rather than leaving the UI half-translated
     * against a catalogue that is not there.
     */
    fun setLocale(tag: String) {
        locale = tag.takeIf { it == DEFAULT_LOCALE || catalogues.containsKey(it) } ?: DEFAULT_LOCALE
        announceDocumentLocale()
    }

    /**
     * Tells the document what language it is in and which way it runs.
     *
     * **The page shipped as a bare `<html>`.** No `lang` at all, which is WCAG 3.1.1 (Language of
     * Page) at Level A and is not a formality: with nothing to go on, a screen reader reads the
     * interface in whatever voice the machine defaults to, so the German build was pronounced by an
     * English synthesiser — every word of it, on every page. Nothing on screen shows this, and
     * nobody who can see the page would ever find it.
     *
     * `dir` comes from the same place because it answers the same question, and because this
     * product cannot credibly ship an interface that is unable to run right-to-left. Today it always
     * resolves to `ltr` — the interface exists in English and German — so this changes nothing you
     * can see. That is the point: the day an Arabic or Hebrew catalogue lands, the layout follows it
     * instead of needing to be rebuilt around it.
     *
     * Failures are swallowed. A missing `documentElement` means there is no document to describe,
     * which is a test harness rather than a problem, and no interface string is worth an exception
     * thrown before the first composition.
     */
    private fun announceDocumentLocale() {
        runCatching {
            document.documentElement?.apply {
                setAttribute("lang", locale)
                setAttribute("dir", textDirection(locale).html)
            }
        }
    }

    /**
     * Reads the stored or preferred locale and installs its catalogue, before anything renders.
     *
     * Synchronous on purpose. This runs from `@InitSilk`, which is not a suspending context, and
     * the alternative — fetch asynchronously and switch when it lands — is exactly the design that
     * broke the app. One small same-origin request at boot, only ever for a non-English locale, is
     * the cheaper trade.
     *
     * Every failure — offline, a 404 for a locale nobody has translated, half-written JSON — leaves
     * the UI in English. A translation layer must not be able to break the page it is translating.
     */
    fun bootstrap() {
        // **The reader's choice, never a guess about it.**
        //
        // This read `navigator.language` when nothing was stored, so a browser set to German got
        // the German catalogue: 31 keys against 3,069. Not a German interface — an English one with
        // thirty-one German strings scattered through it, which is worse than either, and worse
        // precisely for the readers it was meant to help. Nobody chose it and, with no switcher
        // shipped, nobody could undo it.
        //
        // Coverage cannot decide this at runtime: a catalogue holds only the keys somebody has
        // translated, so `de.json` knows its 31 and not the 3,069 it is 31 of, and the denominator
        // is a 260KB fetch away. The honest fix is not a threshold, it is to stop guessing. A
        // stored choice is a choice; `?lang=de` is one too, and it persists so it survives the
        // navigation right after it.
        //
        // The day a catalogue is complete, detection is this comment and one `?:` again.
        val requested = runCatching { localeFromQuery(window.location.search) }.getOrNull()
        val stored = runCatching { localStorage.getItem(LOCALE_KEY) }.getOrNull()
        val wanted = chooseLocale(requested, stored)
        // Before the early return, not after it: English is a language too, and the page needs to
        // say so. Every `return` below this line leaves the document correctly described as the
        // default locale, which is exactly what it is rendering.
        announceDocumentLocale()
        if (wanted == null || wanted == DEFAULT_LOCALE) return
        val body = runCatching {
            val xhr = XMLHttpRequest()
            xhr.open("GET", "/i18n/$wanted.json", async = false)
            xhr.send()
            if (xhr.status.toInt() == 200) xhr.responseText else null
        }.getOrNull() ?: return forget(wanted, stored)
        val entries = runCatching {
            Json.parseToJsonElement(body).jsonObject
                .mapNotNull { (key, value) -> (value as? JsonPrimitive)?.contentOrNull?.let { key to it } }
                .toMap()
        }.getOrNull().orEmpty()
        if (entries.isEmpty()) return forget(wanted, stored)
        register(wanted, entries)
        setLocale(wanted)
        // **Persisted only once it has actually loaded.**
        //
        // The write used to happen the moment `?lang=` was read, before anything checked the
        // catalogue existed. `localeFromQuery` validates the tag's shape and nothing validates that
        // it names a catalogue, so one click on a stale link — `/app/projects?lang=fr`, when only
        // `de.json` and `en.json` ship — overwrote a working German preference with `fr`, 404ed,
        // and left the reader in English with `fr` stored. Every later page load repeated it, and
        // with no switcher in the UI there was nothing they could do about it.
        runCatching { localStorage.setItem(LOCALE_KEY, wanted) }
    }

    /**
     * Drops a stored locale that turned out to have no catalogue.
     *
     * The other half of persisting late. A tag written before this fix, or one whose catalogue is
     * withdrawn later, would otherwise be retried on every page load forever — a permanent English
     * interface for someone whose stored preference says German, with no way to see or clear it.
     * A `?lang=` that fails leaves storage alone, so the previous choice survives the next
     * navigation.
     */
    private fun forget(wanted: String, stored: String?) {
        if (stored == wanted) runCatching { localStorage.removeItem(LOCALE_KEY) }
    }

    /**
     * The locale named by `?lang=` on the current URL, if any.
     *
     * The way a translator, a reviewer or a QA pass sees a catalogue before it is finished — which
     * has to exist, because a catalogue nobody can look at is how one reaches 1% without anybody
     * noticing. Normalised the same way a stored tag is, so `?lang=de-AT` and a stored `de` mean
     * the same catalogue.
     */
    internal fun localeFromQuery(search: String): String? =
        search
            .removePrefix("?")
            .split("&")
            .firstOrNull { it.startsWith("lang=") }
            ?.removePrefix("lang=")
            ?.substringBefore('-')
            ?.lowercase()
            ?.takeIf { it.isNotEmpty() && it.all { c -> c.isLetter() } }

    /**
     * Which catalogue to install, from the only two things allowed to decide it.
     *
     * Separated from [bootstrap] so the policy can be asserted without a browser, a network or a
     * catalogue on disk. What is worth locking down is not the parsing but the shape of the
     * inputs: two, both of them a reader's own act. The version this replaces had a third that was
     * nobody's act, and it is the absent argument that is the fix.
     */
    internal fun chooseLocale(requested: String?, stored: String?): String? =
        (requested ?: stored)?.takeIf { it.isNotEmpty() && it != DEFAULT_LOCALE }

    /**
     * Switches the interface language and reloads.
     *
     * The reload is the mechanism, not a shortcut around one: catalogues are installed before the
     * first composition, so the way to render a different one is to compose again from the top.
     */
    fun switchTo(tag: String) {
        runCatching { localStorage.setItem(LOCALE_KEY, tag) }
        runCatching { window.location.reload() }
    }

    /**
     * The translation for [key], or [source] when there is none.
     *
     * Blank translations are treated as absent. An empty string in a catalogue means "nobody has
     * done this one yet" far more often than it means "this label is deliberately empty", and the
     * failure modes are not comparable: a stray blank costs a reader the label entirely.
     */
    fun translate(key: String, source: String): String {
        if (locale == DEFAULT_LOCALE) return source
        val hit = catalogues[locale]?.get(key)
        return if (hit.isNullOrBlank()) source else hit
    }

    /**
     * The CLDR plural category [count] selects in the active locale — `one`, `other`, `few`…
     *
     * **Asked of the browser, not reimplemented.** `Intl.PluralRules` ships the CLDR rules for every
     * locale it supports, and they are not guessable: Polish uses `few` for 2-4 but `many` for 5+,
     * Arabic distinguishes six categories including `zero` and `two`, and Japanese has only `other`.
     * A hand-rolled `if (n == 1)` is English's rule wearing a general name, which in a product whose
     * entire purpose is getting plurals right would be the most embarrassing possible bug.
     *
     * Falls back to English's two-form behaviour when `Intl.PluralRules` is unavailable or throws on
     * an unknown tag, which is the same degradation the rest of this object makes.
     */
    fun pluralCategory(count: Int): String = runCatching {
        makePluralRules(locale).select(count) as String
    }.getOrElse { if (count == 1) "one" else "other" }

    /**
     * A JS factory value rather than an inline `js("new Intl.PluralRules(locale)")`.
     *
     * The inline form has to name a Kotlin binding inside a JavaScript string, and Kotlin/JS mangles
     * those names — so the snippet threw at runtime and the `runCatching` above quietly fell back to
     * English's rule. Which is the exact bug this function exists to remove, hidden inside its own
     * fix. Handing the tag in as an argument keeps the boundary explicit and name-mangling-proof.
     */
    private val makePluralRules: (String) -> dynamic =
        js("(function (tag) { return new Intl.PluralRules(tag); })")

    internal fun resetForTest() {
        catalogues.clear()
        locale = DEFAULT_LOCALE
    }

    const val DEFAULT_LOCALE = "en"
    private const val LOCALE_KEY = "lingvo:uiLocale"
}

/**
 * The string for [key], falling back to the English [source] written beside it.
 *
 * `@Composable` only so it reads naturally beside the other composables at a call site; the
 * catalogue is fixed before the first composition, so there is nothing to subscribe to.
 */
@Composable
fun t(key: String, source: String): String = I18n.translate(key, source)

/**
 * The non-composable form, for strings built outside composition — toast bodies, `aria-label`s
 * assembled in an event handler, values passed to a plain function.
 *
 * It does not subscribe to locale changes, so a string taken this way is fixed at the moment it is
 * read. That is correct for a toast, which is written once and then stands; it is wrong for a label
 * that must survive a locale switch, and [t] is the one to use there.
 */
fun tr(key: String, source: String): String = I18n.translate(key, source)

/**
 * A counted string, in the number of forms the reader's language actually has.
 *
 * `"$count ${if (count == 1) noun else "${noun}s"}"` is English's rule with a general-sounding
 * name, and it was doing the counting on a page whose own first-run copy sells "real plurals". It is
 * wrong in most languages: Polish needs a third form at five, Arabic needs six, Japanese needs one.
 *
 * The catalogue holds a form per category under `key.one`, `key.other`, `key.few` and so on, and
 * [I18n.pluralCategory] asks the browser which one this count selects. Both English forms stay at
 * the call site for the same reason every other string does — a missing catalogue renders readable
 * English rather than a key — and `{count}` is substituted last so a translation can put the number
 * where its own grammar wants it.
 */
fun tPlural(
    key: String,
    count: Int,
    one: String,
    other: String,
    vararg args: Pair<String, Any?>,
): String {
    val category = I18n.pluralCategory(count)
    val fallback = if (category == "one") one else other
    val form = if (I18n.locale == I18n.DEFAULT_LOCALE) {
        fallback
    } else {
        I18n.translate("$key.$category", "").ifBlank { I18n.translate("$key.other", fallback) }
    }
    // `{count}` first, then everything else, so a message may carry both — "{names} and {count}
    // others are editing this" is one sentence whose verb agrees with the count and whose subject
    // is a list, and splitting it into two lookups would hand a translator two fragments neither of
    // which can be made to agree with the other.
    return substituteNamed(form.replace("{count}", count.toString()), args)
}

/**
 * A message with values dropped into it, named rather than positional.
 *
 * **A Kotlin template cannot be translated.** `"Translate $name"` is one expression to the compiler
 * and no string at all to the extractor, so 134 of these — 23 of them `aria-label`s — stayed English
 * in every locale while the interface around them changed language. A reader on a Japanese interface
 * heard "Translate checkout.confirm" from their screen reader because the sentence was never a
 * string anybody could offer a translator.
 *
 * Named placeholders rather than `%s` or `{0}`, because word order is exactly what changes between
 * languages: German puts the verb last and Japanese puts the particle after the noun, so a
 * translation has to be free to move `{name}` anywhere in the sentence — or to use it twice, or to
 * drop it. Positional arguments only work when every language agrees on order, which is the
 * assumption that made these templates untranslatable in the first place.
 *
 * A placeholder with no matching argument is **left standing**, so `{nmae}` renders as `{nmae}`
 * rather than as a hole where the reader's project name should have been. The visible failure is the
 * point: a silent one hands back a sentence that reads fine and is missing its subject. The agreement
 * between the English source and the argument list is checked statically by
 * `scripts/extract-strings.mjs`, so the misspelling never reaches a locale file to begin with.
 */
@Composable
fun tf(key: String, source: String, vararg args: Pair<String, Any?>): String =
    substituteNamed(I18n.translate(key, source), args)

/**
 * The non-composable form of [tf], for labels assembled in an event handler or outside composition.
 *
 * Same relationship to [tf] as [tr] has to [t], and the same caveat: fixed at the moment it is read.
 */
fun trf(key: String, source: String, vararg args: Pair<String, Any?>): String =
    substituteNamed(I18n.translate(key, source), args)

/**
 * Replaces `{name}` with the argument called `name`, and nothing else.
 *
 * **One pass over the message, never over what it produced.** Replacing arguments one at a time
 * with `String.replace` re-reads its own output, so a value containing `{author}` would be
 * substituted again by a later argument of that name. On a generic product that is a curiosity; on
 * this one it is Tuesday, because the strings this interface displays *are* other people's
 * placeholders — a QA exemption reason, a key name, a translation. Scanning the template once and
 * copying values in verbatim makes an argument's content inert by construction.
 *
 * A brace that names no argument is copied through rather than removed: a message may legitimately
 * contain one — a code sample, a placeholder the reader is meant to type — and a misspelling must
 * stay visible rather than leave a hole where a value should have been.
 *
 * A null argument renders empty rather than the word "null", which is what a missing middle name or
 * an absent count should look like in a sentence.
 */
private fun substituteNamed(text: String, args: Array<out Pair<String, Any?>>): String {
    if (args.isEmpty()) return text
    val builder = StringBuilder(text.length)
    var cursor = 0
    while (cursor < text.length) {
        val open = text.indexOf('{', cursor)
        if (open < 0) {
            builder.append(text, cursor, text.length)
            break
        }
        val close = text.indexOf('}', open + 1)
        val name = if (close > open) text.substring(open + 1, close) else null
        val match = name?.let { candidate -> args.firstOrNull { it.first == candidate } }
        if (match == null) {
            builder.append(text, cursor, open + 1)
            cursor = open + 1
        } else {
            builder.append(text, cursor, open)
            builder.append(match.second?.toString().orEmpty())
            cursor = close + 1
        }
    }
    return builder.toString()
}

/**
 * Installs the interface catalogue before the first composition.
 *
 * `@InitSilk` rather than a `LaunchedEffect`: by the time an effect runs, the composition it would
 * change is already on screen, and switching under it is what threw "duplicate keyframe name".
 */
@InitSilk
fun initI18n(ctx: InitSilkContext) {
    I18n.bootstrap()
}
